export const GA_MEASUREMENT_ID = 'G-1HPKD7XB7R';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

export type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Checks whether the current runtime environment is a local test, development,
 * preview, or private LAN address. Analytics transmissions to Google Analytics are disabled
 * by default on local/test environments to prevent polluting production reports.
 */
export function isLocalEnvironment(options?: { ignoreVitest?: boolean }): boolean {
  if (typeof window === 'undefined') return true;
  if (!window.location || !window.location.hostname) return true;

  // Allow explicit opt-in for testing telemetry in local environments:
  try {
    const search = window.location.search || '';
    if (search.includes('force_analytics=true') || search.includes('enable_analytics=true')) {
      return false;
    }
    if (
      typeof window.localStorage !== 'undefined' &&
      (window.localStorage.getItem('force_analytics') === 'true' ||
        window.localStorage.getItem('enable_analytics') === 'true')
    ) {
      return false;
    }
  } catch {
    // ignore security / storage restrictions
  }

  // Vitest test runner check: in Vitest integration tests, we want to allow window.gtag spies
  // to record and assert on events unless the test is specifically testing local exclusion.
  if (!options?.ignoreVitest) {
    try {
      if (typeof process !== 'undefined' && (process as unknown as { env?: Record<string, string> }).env?.VITEST) {
        return false;
      }
    } catch {
      // ignore
    }
  }

  // Vite DEV mode
  try {
    if (import.meta.env.DEV) return true;
  } catch {
    // ignore
  }

  const h = window.location.hostname.toLowerCase();
  if (
    !h ||
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]' ||
    h === '0.0.0.0' ||
    h.endsWith('.local') ||
    h.endsWith('.localhost') ||
    h.endsWith('.test') ||
    h.endsWith('.example') ||
    h.endsWith('.invalid') ||
    /^192\.168\./.test(h) ||
    /^10\./.test(h) ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(h)
  ) {
    return true;
  }

  return false;
}

// Automatically disable Google Analytics tag if on local environment outside of vitest runner
if (typeof window !== 'undefined' && isLocalEnvironment({ ignoreVitest: true })) {
  try {
    if (typeof process === 'undefined' || !process.env?.VITEST) {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    }
  } catch {
    // ignore
  }
}

function callGtag(...args: unknown[]): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (window[`ga-disable-${GA_MEASUREMENT_ID}`] || isLocalEnvironment()) {
    try {
      if (
        (typeof window.localStorage !== 'undefined' &&
          window.localStorage.getItem('debug_analytics') === 'true') ||
        (window.location?.search && window.location.search.includes('debug_analytics=true'))
      ) {
        console.log('[Analytics Mock (Local Excluded)]', ...args);
      }
    } catch {
      // ignore
    }
    return;
  }
  try {
    window.gtag(...args);
  } catch {
    // Never let analytics errors interrupt gameplay
  }
}

export function trackEvent(eventName: string, params?: AnalyticsParams): void {
  if (!params) {
    callGtag('event', eventName);
    return;
  }
  const cleaned: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) {
      cleaned[k] = v;
    }
  }
  callGtag('event', eventName, cleaned);
}

let lastTrackedPath = '';
let lastTrackedAt = 0;

export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (pagePath === lastTrackedPath && now - lastTrackedAt < 1000) return;
  lastTrackedPath = pagePath;
  lastTrackedAt = now;

  callGtag('event', 'page_view', {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: pageTitle ?? (typeof document !== 'undefined' ? document.title : ''),
    send_to: GA_MEASUREMENT_ID,
  });
}

export function setAnalyticsUser(
  profile: {
    id: string;
    tier: string;
    isAdmin?: boolean;
    tsaiBalance?: number;
  } | null,
): void {
  if (!profile) {
    callGtag('config', GA_MEASUREMENT_ID, { user_id: null });
    return;
  }
  callGtag('config', GA_MEASUREMENT_ID, {
    user_id: profile.id,
  });
  callGtag('set', 'user_properties', {
    user_tier: profile.tier,
    is_admin: profile.isAdmin ? 'true' : 'false',
    tsai_balance_bucket:
      typeof profile.tsaiBalance === 'number'
        ? profile.tsaiBalance >= 200
          ? '200+'
          : profile.tsaiBalance >= 50
          ? '50-199'
          : '0-49'
        : 'unknown',
  });
}
